import React, { useState, useRef, useEffect } from "react";
import { Info, X, HelpCircle, Award } from "lucide-react";

/**
 * Componente interactivo de Información Clínica y Ocupacional para Widgets B2B.
 * Muestra:
 * 1. Qué está viendo el usuario (definición clínica)
 * 2. Cómo se calculó (fórmula / criterio clínico)
 * 3. Estándar / Consenso Internacional que lo respalda (ACC/AHA, ADA, IDF, OMS, etc.)
 * 4. Por qué es relevante para SST y RRHH
 */
export default function WidgetInfoTooltip({
  title,
  what,
  calculation,
  standard,
  relevance,
  align = "right", // "left" | "right" | "center"
  size = "md", // "md" | "sm"
  className = "",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Cerrar al hacer clic fuera del tooltip
  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isOpen]);

  // Cerrar con Escape
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === "Escape") setIsOpen(false);
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const getAlignmentClass = () => {
    if (align === "left") return "left-0";
    if (align === "center") return "left-1/2 -translate-x-1/2";
    return "right-0"; // default
  };

  const isSmall = size === "sm";

  return (
    <div className={`relative inline-flex items-center ${className}`} ref={containerRef}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        aria-label={`Información sobre ${title || "esta métrica"}`}
        title="Ver detalles, metodología de cálculo y estándar internacional"
        data-testid="widget-info-btn"
        className={`rounded-full flex items-center justify-center transition-all cursor-pointer ${
          isSmall ? "w-4 h-4" : "w-6 h-6"
        } ${
          isOpen
            ? "bg-slate-800 text-white shadow-sm ring-2 ring-slate-300"
            : "text-slate-400 hover:text-slate-700 hover:bg-slate-100/80"
        }`}
      >
        <Info size={isSmall ? 10 : 13.5} />
      </button>

      {/* Popover flotante con alto contraste */}
      {isOpen && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="widget-info-popover"
          style={{ backgroundColor: "#0F172A", opacity: 1 }}
          className={`absolute top-full mt-2 w-80 sm:w-96 p-4 text-slate-100 rounded-2xl shadow-2xl border border-slate-700/80 z-50 text-left ${getAlignmentClass()}`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Cabecera */}
          <div className="flex items-start justify-between gap-2 pb-2.5 mb-3 border-b border-slate-800">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-md bg-teal-500/20 text-teal-400 flex items-center justify-center">
                <HelpCircle size={13} />
              </div>
              <h4 className="text-xs font-bold text-white tracking-wide">
                {title || "Información Epidemiológica"}
              </h4>
            </div>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="text-slate-400 hover:text-white transition-colors p-0.5 rounded-md hover:bg-slate-800"
              aria-label="Cerrar detalle"
            >
              <X size={14} />
            </button>
          </div>

          <div className="space-y-3 text-xs leading-relaxed">
            {/* 1. Qué está viendo */}
            {what && (
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-teal-400 flex items-center gap-1 mb-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-400 inline-block" />
                  ¿Qué representa esta métrica?
                </span>
                <p className="text-slate-300 text-[11.5px]">{what}</p>
              </div>
            )}

            {/* 2. Cómo se calculó */}
            {calculation && (
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-sky-400 flex items-center gap-1 mb-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-sky-400 inline-block" />
                  Metodología de cálculo
                </span>
                <p
                  style={{ backgroundColor: "#1E293B" }}
                  className="text-slate-300 text-[11px] p-2.5 rounded-lg border border-slate-700/60 font-mono"
                >
                  {calculation}
                </p>
              </div>
            )}

            {/* 3. Estándar / Consenso Internacional */}
            {standard && (
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-400 flex items-center gap-1 mb-0.5">
                  <Award size={11} className="text-emerald-400" />
                  Estándar / Consenso Internacional
                </span>
                <p
                  style={{ backgroundColor: "#064E3B", borderColor: "#047857" }}
                  className="text-emerald-200 text-[11px] p-2 rounded-lg border font-medium leading-relaxed"
                >
                  {standard}
                </p>
              </div>
            )}

            {/* 4. Por qué es relevante */}
            {relevance && (
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-amber-400 flex items-center gap-1 mb-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" />
                  Impacto en SST y RRHH
                </span>
                <p className="text-slate-300 text-[11.5px]">{relevance}</p>
              </div>
            )}
          </div>

          {/* Pie informativo */}
          <div className="mt-3 pt-2 border-t border-slate-800/80 flex items-center justify-between text-[10px] text-slate-400">
            <span>Confidencialidad médica garantizada</span>
            <span className="text-teal-400 font-medium">WE360 Analytics</span>
          </div>
        </div>
      )}
    </div>
  );
}
